import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  TenantConfig,
  DEFAULT_CONFIG,
  CURRENCY_SYMBOLS,
  CURRENCY_DECIMALS,
  type SupportedCurrency,
} from "./tenant-config";

const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  'SAR', 'AED', 'KWD', 'BHD', 'OMR', 'QAR', 'USD', 'EUR', 'GBP',
];

function isSupportedCurrency(value: unknown): value is SupportedCurrency {
  return typeof value === 'string' && (SUPPORTED_CURRENCIES as string[]).includes(value);
}

/**
 * Read the active channel's defaultCurrencyCode from the persisted auth data.
 * Returns null if no channel is active or the stored data lacks a currency.
 *
 * Critical for price writes: dashboard mutations must use the tenant channel's
 * currency (not the default channel's USD) when creating ProductVariantPrice
 * rows — otherwise prices land in the wrong (channel, currency) row and the
 * tenant's price stays at 0.
 */
function readActiveChannelCurrency(): SupportedCurrency | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('kayn_active_channel');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isSupportedCurrency(parsed?.defaultCurrencyCode)
      ? parsed.defaultCurrencyCode
      : null;
  } catch {
    return null;
  }
}

interface TenantContextValue {
  config: TenantConfig;
  /** Format a price in cents to display string */
  formatPrice: (priceInCents: number, currencyOverride?: SupportedCurrency) => string;
  /** Format a date to localized string */
  formatDate: (date: string | Date, format?: "short" | "long" | "withTime") => string;
  /** Format a number with locale */
  formatNumber: (num: number) => string;
  /** Get currency symbol */
  getCurrencySymbol: (currency?: SupportedCurrency) => string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: React.ReactNode;
  config?: Partial<TenantConfig>;
}

export function TenantProvider({ children, config: configOverrides }: TenantProviderProps) {
  // Track the active channel's currency. On hydration, read from the persisted
  // auth data; on subsequent channel switches, listen for storage events so
  // multi-tab and same-tab updates stay in sync.
  const [activeCurrency, setActiveCurrency] = useState<SupportedCurrency | null>(
    typeof window !== 'undefined' ? readActiveChannelCurrency() : null,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => setActiveCurrency(readActiveChannelCurrency());
    window.addEventListener('storage', sync);
    // Custom event so same-tab channel switches (auth-context) can notify us
    window.addEventListener('kayn:channel-switched', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('kayn:channel-switched', sync);
    };
  }, []);

  const config = useMemo<TenantConfig>(
    () => ({
      ...DEFAULT_CONFIG,
      ...configOverrides,
      // Active channel's currency wins over DEFAULT_CONFIG; explicit
      // configOverrides.currency (rare) still wins over both.
      currency: configOverrides?.currency ?? activeCurrency ?? DEFAULT_CONFIG.currency,
    }),
    [configOverrides, activeCurrency]
  );

  const formatPrice = useMemo(() => {
    return (priceInCents: number, currencyOverride?: SupportedCurrency): string => {
      const currency = currencyOverride || config.currency;
      const symbol = CURRENCY_SYMBOLS[currency] || currency;
      const decimals = CURRENCY_DECIMALS[currency] ?? config.numberFormat.minimumFractionDigits;
      const formatted = (priceInCents / 1000).toLocaleString(config.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return `${formatted} ${symbol}`;
    };
  }, [config]);

  const formatDate = useMemo(() => {
    return (date: string | Date, format: "short" | "long" | "withTime" = "short"): string => {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      const options = config.dateFormat[format];
      return dateObj.toLocaleDateString(config.locale, options);
    };
  }, [config]);

  const formatNumber = useMemo(() => {
    return (num: number): string => {
      return num.toLocaleString(config.locale, config.numberFormat);
    };
  }, [config]);

  const getCurrencySymbol = useMemo(() => {
    return (currency?: SupportedCurrency): string => {
      return CURRENCY_SYMBOLS[currency || config.currency] || config.currency;
    };
  }, [config]);

  const value = useMemo<TenantContextValue>(
    () => ({
      config,
      formatPrice,
      formatDate,
      formatNumber,
      getCurrencySymbol,
    }),
    [config, formatPrice, formatDate, formatNumber, getCurrencySymbol]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/**
 * Hook to access tenant configuration and utilities
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
